import {
    buildReducer,
    ContactFolder,
    createMapOfSet,
    getLogger,
    uniq,
    SparseFolderLoadedState,
    contactsLeftNavItemId,
} from '../../../shared';
import { ContactFoldersState, FolderLoadingState } from '../../state/data/contactFoldersState';
import {
    loadPersonasCompletedAction,
    loadPersonasInSparseFolderCompletedAction,
    contactFoldersLoadCompletedAction,
    getFoldersCompletedAction,
    deletePersonasCompletedAction,
    deletePublicFolderPersonaCompletedAction,
    contactFoldersSubscribedToNotificationsAction,
    contactFoldersUnsubscribedToNotificationsAction,
    linkPersonasCompletedAction,
    moveContactsCompletedAction,
    deletedItemsFoldersLoadCompletedAction,
    contactEditorNewContactSaveClickedAction,
} from '../../../hub';
import { privateDLCreateCompletedAction } from '../../../privatedlcreateedit';

import {
    createFolderCompletedAction,
    deleteFolderCompletedAction,
    renameFolderCompletedAction,
    hardDeleteFolderCompletedAction,
    moveFolderCompletedAction,
    addPublicFolderToFavoritesCompletedAction,
    removeFavoritePublicFolderCompletedAction,
} from '../../../appchrome';
import { lpcPersonaCreatedAction } from '../../actions/lpcActions';

export type { ContactFoldersState };

export const defaultState: ContactFoldersState = {
    folderIdToHubPersonaIds: {},
    folderLoadingState: {},
    folderErrorState: {},
    folderIdToInitialLoadBatchId: {},
    rootContactFolder: undefined,
    displayedContactFolders: [],
    deletedItemsFolders: [],
    publicFolders: [],
    sparseFolders: [],
    sparseFoldersState: {},
    defaultContactFolder: undefined,
    shouldProcessUpdateNotifications: false,
    uninitializedFoldersWithKnownPersona: {},
};

export default buildReducer<ContactFoldersState>()
    .handleDataAction(
        loadPersonasCompletedAction,
        (state, action) => {
            const personaHubIds = (action.payload.personas || []).map(
                persona => persona.hubPersonaId
            );

            if (state.folderLoadingState[action.payload.folderId] !== FolderLoadingState.Done) {
                // We are loading the folder for the first time
                if (action.payload.isFirstBatch && action.payload.isLastBatch) {
                    return getAllPersonasReceivedUpdate(
                        state,
                        action.payload.folderId,
                        personaHubIds
                    );
                } else if (action.payload.isFirstBatch && !action.payload.isLastBatch) {
                    return getFirstBatchOfMultipleUpdates(
                        state,
                        action.payload.folderId,
                        action.payload.batchId,
                        personaHubIds
                    );
                } else if (!action.payload.isFirstBatch && !action.payload.isLastBatch) {
                    return getIntermediateBatchUpdates(
                        state,
                        action.payload.folderId,
                        action.payload.batchId,
                        personaHubIds
                    );
                } else {
                    return getLastBatchOfMultipleUpdates(
                        state,
                        action.payload.folderId,
                        action.payload.batchId,
                        personaHubIds
                    );
                }
            } else {
                // Folder has been populated before. We only care about the last batch.
                if (action.payload.isLastBatch) {
                    return getAllPersonasReceivedUpdate(
                        state,
                        action.payload.folderId,
                        personaHubIds
                    );
                }
                return state;
            }
        },
        (state, action) => {
            return {
                ...state,
                folderIdToHubPersonaIds: {
                    ...state.folderIdToHubPersonaIds,
                    [action.payload.folderId]: [],
                },
                folderLoadingState: {
                    ...state.folderLoadingState,
                    [action.payload.folderId]: FolderLoadingState.Error,
                },
                folderErrorState: {
                    ...state.folderErrorState,
                    [action.payload.folderId]: action.payload.error,
                },
            };
        }
    )
    .handleDataAction(loadPersonasInSparseFolderCompletedAction, (state, action) => {
        const folderId: string = action.payload.folderId;

        const updatedLoadedState: SparseFolderLoadedState =
            (state.sparseFoldersState[folderId] && {
                ...state.sparseFoldersState[folderId].loaded,
            }) ||
            {};

        let index: number = action.payload.offset;
        action.payload.personas.forEach(persona => {
            updatedLoadedState[index++] = persona.hubPersonaId;
        });

        return {
            ...state,
            folderLoadingState: {
                ...state.folderLoadingState,
                [folderId]: FolderLoadingState.InProgress,
            },
            folderErrorState: {
                ...state.folderErrorState,
                [folderId]: undefined,
            },
            sparseFoldersState: {
                ...state.sparseFoldersState,
                [action.payload.folderId]: {
                    ...state.sparseFoldersState[action.payload.folderId],
                    totalCount: action.payload.totalCount,
                    loaded: updatedLoadedState,
                },
            },
        };
    })
    .handleDataAction(getFoldersCompletedAction, (state, action) => {
        const updatedPublicFolders = [...state.publicFolders];
        action.payload.forEach(payloadFolder => {
            const updatedFolderIndex = updatedPublicFolders.findIndex(
                updatedFolder => updatedFolder.id === payloadFolder.folderId
            );
            if (updatedFolderIndex !== -1) {
                const updatedFolder = {
                    ...updatedPublicFolders[updatedFolderIndex],
                    publicFolderInfo: {
                        effectiveRights: { ...payloadFolder.effectiveRights },
                        folderSmtpAddress: payloadFolder.folderSmtpAddress,
                    },
                };
                updatedPublicFolders[updatedFolderIndex] = updatedFolder;
            }
        });

        return {
            ...state,
            publicFolders: filterInvalidPublicFolders(updatedPublicFolders),
        };
    })
    .handleDataAction(deletePersonasCompletedAction, (state, action) => {
        // Remove personas from all folders
        const newMap = { ...state.folderIdToHubPersonaIds };
        const beingDeletedMap = createMapOfSet(action.payload.deletedHubPersonaIds);
        Object.keys(state.folderIdToHubPersonaIds).forEach(folderId => {
            newMap[folderId] = state.folderIdToHubPersonaIds[folderId].filter(
                hubPersonaId => !beingDeletedMap[hubPersonaId]
            );
        });

        return {
            ...state,
            folderIdToHubPersonaIds: newMap,
        };
    })
    .handleDataAction(deletePublicFolderPersonaCompletedAction, (state, action) => {
        // Remove persona from all folders
        const newMap = { ...state.folderIdToHubPersonaIds };
        const deletedId = action.payload.deletedHubPersonaId;
        Object.keys(state.folderIdToHubPersonaIds).forEach(folderId => {
            newMap[folderId] = state.folderIdToHubPersonaIds[folderId].filter(
                id => id !== deletedId
            );
        });

        return {
            ...state,
            folderIdToHubPersonaIds: newMap,
        };
    })
    .handleDataAction(contactFoldersLoadCompletedAction, (state, action) => {
        const folders = action.payload;

        return {
            ...state,
            rootContactFolder: folders.rootContactFolder,
            defaultContactFolder: folders.defaultContactFolder,
            displayedContactFolders: folders.displayedFolders,
            publicFolders: filterInvalidPublicFolders(folders.publicFolders),
            sparseFolders: folders.sparseFolders,
        };
    })
    .handleDataAction(deletedItemsFoldersLoadCompletedAction, (state, action) => {
        return { ...state, deletedItemsFolders: action.payload };
    })
    .handleDataAction(privateDLCreateCompletedAction, (state, action) => {
        // only add newly created persona to a folder if loading isn't currently in progress
        if (state.folderLoadingState[action.payload.folderId] !== FolderLoadingState.InProgress) {
            return {
                ...state,
                folderIdToHubPersonaIds: {
                    ...state.folderIdToHubPersonaIds,
                    [action.payload.folderId]: [
                        ...(state.folderIdToHubPersonaIds[action.payload.folderId] || []),
                        action.payload.privateDL.hubPersonaId,
                    ],
                },
                uninitializedFoldersWithKnownPersona: {
                    ...state.uninitializedFoldersWithKnownPersona,
                    [action.payload.folderId]: action.payload.privateDL.hubPersonaId,
                },
            };
        }

        return state;
    })
    .handleDataAction(createFolderCompletedAction, (state, action) => ({
        ...state,
        displayedContactFolders: [...state.displayedContactFolders, action.payload.newFolder],
    }))
    .handleDataAction(linkPersonasCompletedAction, (state, action) => {
        // Remove linked contacts from folder-persona mapping
        const folderIdToHubPersonaIds = { ...state.folderIdToHubPersonaIds };
        Object.keys(folderIdToHubPersonaIds).forEach(
            folderId =>
                (folderIdToHubPersonaIds[folderId] = folderIdToHubPersonaIds[folderId].filter(
                    personaId =>
                        !action.payload.oldHubPersonaIds.some(
                            oldPersonaId =>
                                oldPersonaId === personaId &&
                                oldPersonaId !== action.payload.linkedPersona.hubPersonaId
                        )
                ))
        );

        return {
            ...state,
            folderIdToHubPersonaIds: folderIdToHubPersonaIds,
        };
    })
    .handleDataAction(renameFolderCompletedAction, (state, action) => {
        const folderToRenameIndex = state.displayedContactFolders.findIndex(
            folder => folder.id === action.payload.folderId
        );

        if (folderToRenameIndex >= 0) {
            const newDisplayedContactsFolders = [...state.displayedContactFolders];
            newDisplayedContactsFolders[folderToRenameIndex] = {
                ...newDisplayedContactsFolders[folderToRenameIndex],
                displayName: action.payload.newName,
            };

            return {
                ...state,
                displayedContactFolders: newDisplayedContactsFolders,
            };
        }

        return state;
    })
    .handleDataAction(deleteFolderCompletedAction, (state, action) => {
        const deletedFolderIndex = state.displayedContactFolders.findIndex(
            folder => folder.id === action.payload.folderId
        );
        const deletedFolder = { ...state.displayedContactFolders[deletedFolderIndex] };

        const defaultFolderContacts = state.folderIdToHubPersonaIds[state.rootContactFolder.id];
        const deletedFolderContactsMap = createMapOfSet(
            state.folderIdToHubPersonaIds[deletedFolder.id]
        );

        const newDefaultFolderContacts = defaultFolderContacts.filter(
            contact => !deletedFolderContactsMap[contact]
        );

        return {
            ...state,
            folderIdToHubPersonaIds: {
                ...state.folderIdToHubPersonaIds,
                [state.rootContactFolder.id]: newDefaultFolderContacts,
            },
            displayedContactFolders: state.displayedContactFolders.filter(
                folder => folder.id !== action.payload.folderId
            ),
            deletedItemsFolders: [...state.deletedItemsFolders, deletedFolder],
        };
    })
    .handleDataAction(removeFavoritePublicFolderCompletedAction, (state, action) => {
        return {
            ...state,
            publicFolders: state.publicFolders.filter(
                folder => folder.id !== action.payload.folderId
            ),
        };
    })
    .handleDataAction(hardDeleteFolderCompletedAction, (state, action) => ({
        ...state,
        deletedItemsFolders: state.deletedItemsFolders.filter(
            folder => folder.id !== action.payload.folderId
        ),
    }))
    // TODO: This handles only one case, when we move from DeletedItems to DefaultFolder
    // There is no other Move operation for folders available in PH.
    .handleDataAction(moveFolderCompletedAction, (state, action) => {
        if (action.payload.toFolderId === state.defaultContactFolder.id) {
            const restoredFolderIndex = state.deletedItemsFolders.findIndex(
                folder => folder.id === action.payload.folderId
            );
            const restoredFolder = { ...state.deletedItemsFolders[restoredFolderIndex] };
            const displayedContactFolders = [...state.displayedContactFolders, restoredFolder];
            const deletedItemsFolders = state.deletedItemsFolders.filter(
                folder => folder.id !== action.payload.folderId
            );
            return { ...state, displayedContactFolders, deletedItemsFolders };
        }
        return { ...state };
    })
    .handleDataAction(moveContactsCompletedAction, (state, action) => {
        const newMap = { ...state.folderIdToHubPersonaIds };
        newMap[action.payload.toFolderId] = uniq([
            ...(newMap[action.payload.toFolderId] || []),
            ...action.payload.personas.map(persona => persona.hubPersonaId),
        ]);
        newMap[action.payload.fromFolderId] = newMap[action.payload.fromFolderId].filter(
            hubPersonaId =>
                !action.payload.personas.some(persona => persona.hubPersonaId === hubPersonaId)
        );

        return {
            ...state,
            folderIdToHubPersonaIds: newMap,
        };
    })
    .handle(lpcPersonaCreatedAction, (state, action) => {
        // only add newly created persona to a folder if loading isn't in progress
        if (state.folderLoadingState[action.payload.folderId] !== FolderLoadingState.InProgress) {
            // If newly created persona is auto-linked to existing persona in the same folder by backend,
            // the hubPersonaId of the new persona will match what already exists in folderIdToHubPersonaIds,
            // so need to run uniq on the updated array of hubPersonaIds
            return {
                ...state,
                folderIdToHubPersonaIds: {
                    ...state.folderIdToHubPersonaIds,
                    [action.payload.folderId]: uniq([
                        ...(state.folderIdToHubPersonaIds[action.payload.folderId] || []),
                        action.payload.persona.hubPersonaId,
                    ]),
                },
                uninitializedFoldersWithKnownPersona: {
                    ...state.uninitializedFoldersWithKnownPersona,
                    [action.payload.folderId]: action.payload.persona.hubPersonaId,
                },
            };
        }

        return state;
    })
    .handle(contactEditorNewContactSaveClickedAction, (state, { payload }) => {
        // Only call if contact editor is on CAPIv3 i.e. hubContact exists
        // for CAPIv3, we only have usercuratedcontacts folder
        const folderId =
            payload.folderId === contactsLeftNavItemId
                ? state.rootContactFolder.id
                : payload.folderId;

        if (
            payload.hubContact &&
            state.folderLoadingState[folderId] !== FolderLoadingState.InProgress
        ) {
            return {
                ...state,
                folderIdToHubPersonaIds: {
                    [folderId]: uniq([
                        ...(state.folderIdToHubPersonaIds[folderId] || []),
                        payload.hubContact.hubPersonaId,
                    ]),
                },
                uninitializedFoldersWithKnownPersona: {
                    ...state.uninitializedFoldersWithKnownPersona,
                    [folderId]: payload.hubContact.hubPersonaId,
                },
            };
        }

        return state;
    })
    .handle(contactFoldersSubscribedToNotificationsAction, state => ({
        ...state,
        shouldProcessUpdateNotifications: true,
    }))
    .handle(contactFoldersUnsubscribedToNotificationsAction, state => ({
        ...state,
        shouldProcessUpdateNotifications: false,
    }))
    .handle(addPublicFolderToFavoritesCompletedAction, (state, action) => {
        // Do not add the public folder if it already exists in the left nav. This is a
        // safegueard due different stores used by Mail and People.
        if (state.publicFolders.some(folder => folder.id === action.payload.folder.id)) {
            return state;
        }

        const updatedPublicFolders = [...state.publicFolders].concat(action.payload.folder);

        return {
            ...state,
            publicFolders: filterInvalidPublicFolders(updatedPublicFolders),
        };
    })
    .done(defaultState);

function getAllPersonasReceivedUpdate(
    state: ContactFoldersState,
    folderId: string,
    personaHubIds: string[]
): ContactFoldersState {
    return {
        ...state,
        folderIdToHubPersonaIds: {
            ...state.folderIdToHubPersonaIds,
            [folderId]: personaHubIds,
        },
        folderLoadingState: {
            ...state.folderLoadingState,
            [folderId]: FolderLoadingState.Done,
        },
        folderErrorState: {
            ...state.folderErrorState,
            [folderId]: undefined,
        },
    };
}

function getFirstBatchOfMultipleUpdates(
    state: ContactFoldersState,
    folderId: string,
    batchId: string,
    personaHubIds: string[]
): ContactFoldersState {
    const haveSeenKnownPersona =
        state.uninitializedFoldersWithKnownPersona[folderId] &&
        personaHubIds.some(id => state.uninitializedFoldersWithKnownPersona[folderId] === id);
    return {
        ...state,
        folderIdToHubPersonaIds: {
            ...state.folderIdToHubPersonaIds,
            [folderId]: haveSeenKnownPersona
                ? personaHubIds
                : [state.uninitializedFoldersWithKnownPersona[folderId]]
                      .filter(element => !!element)
                      .concat(personaHubIds),
        },
        folderIdToInitialLoadBatchId: {
            ...state.folderIdToInitialLoadBatchId,
            [folderId]: batchId,
        },
        folderLoadingState: {
            ...state.folderLoadingState,
            [folderId]: FolderLoadingState.InProgress,
        },
        folderErrorState: {
            ...state.folderErrorState,
            [folderId]: undefined,
        },
        uninitializedFoldersWithKnownPersona: {
            ...state.uninitializedFoldersWithKnownPersona,
            [folderId]: haveSeenKnownPersona
                ? undefined
                : state.uninitializedFoldersWithKnownPersona[folderId],
        },
    };
}

function getIntermediateBatchUpdates(
    state: ContactFoldersState,
    folderId: string,
    batchId: string,
    personaHubIds: string[]
): ContactFoldersState {
    if (state.folderIdToInitialLoadBatchId[folderId] === batchId) {
        const haveSeenKnownPersona =
            state.uninitializedFoldersWithKnownPersona[folderId] &&
            personaHubIds.some(id => state.uninitializedFoldersWithKnownPersona[folderId] === id);
        return {
            ...state,
            folderIdToHubPersonaIds: {
                ...state.folderIdToHubPersonaIds,
                [folderId]: haveSeenKnownPersona
                    ? personaHubIds
                    : [state.uninitializedFoldersWithKnownPersona[folderId]]
                          .filter(element => !!element)
                          .concat(personaHubIds),
            },
            uninitializedFoldersWithKnownPersona: {
                ...state.uninitializedFoldersWithKnownPersona,
                [folderId]: haveSeenKnownPersona
                    ? undefined
                    : state.uninitializedFoldersWithKnownPersona[folderId],
            },
        };
    }
    return state;
}

function getLastBatchOfMultipleUpdates(
    state: ContactFoldersState,
    folderId: string,
    batchId: string,
    personaHubIds: string[]
): ContactFoldersState {
    if (state.folderIdToInitialLoadBatchId[folderId] === batchId) {
        return {
            ...state,
            folderIdToHubPersonaIds: {
                ...state.folderIdToHubPersonaIds,
                [folderId]: personaHubIds,
            },
            folderLoadingState: {
                ...state.folderLoadingState,
                [folderId]: FolderLoadingState.Done,
            },
            folderErrorState: {
                ...state.folderErrorState,
                [folderId]: undefined,
            },
            uninitializedFoldersWithKnownPersona: {
                ...state.uninitializedFoldersWithKnownPersona,
                [folderId]: undefined,
            },
        };
    }
    return state;
}

/// In some instances public folders with null ID or empty ID are returned. These are not actionable,
/// and should be removed for the end user
function filterInvalidPublicFolders(contactFolders: ContactFolder[]): ContactFolder[] {
    if (contactFolders.some(contactFolder => contactFolder.id === null)) {
        logPublicFolderIdError('Folder Id is null');
    } else if (contactFolders.some(contactFolder => contactFolder.id === undefined)) {
        logPublicFolderIdError('Folder Id is undefined');
    } else if (contactFolders.some(contactFolder => contactFolder.id === '')) {
        logPublicFolderIdError('Folder Id is empty string');
    }
    return contactFolders.filter(contactFolder => !!contactFolder.id);
}

function logPublicFolderIdError(error: string) {
    getLogger().log('FolderReducer', {
        component: 'publicFolder',
        error: error,
    });
}
export type { Action } from '../../../shared';
